<template>
  <div id="CoachingPlatform">
    <div class="coaching-container">
      <div class="platform-left">
        <div class="heading-wrapper">
          <h2 class="heading">Your Upcoming Coaching Session</h2>
          <div class="line"></div>
        </div>
        <div class="skeleton-placeholder" v-if="!upcomingBookings">
          <div class="skeleton-item" v-for="n in 3" :key="n"></div>
        </div>
        <div
          v-else-if="upcomingBookings && upcomingBookings.length"
          class="booking-list"
        >
          <BookingWithCoachCard
            v-for="booking in upcomingBookings"
            :key="booking.id"
            :booking="booking"
          />
        </div>
        <div v-else class="mb-5">
          <p class="booking-text">Sorry you don't have any bookings yet</p>
        </div>
        <div class="heading-wrapper">
          <h2 class="heading">Your Journey</h2>
          <div class="line"></div>
        </div>
        <div v-if="orphanTasks && orphanTasks.length" class="orphan-tasks shadow">
          <p class="orphan-tasks-text">
            Continue to grow with bonus tasks
          </p>
          <div v-for="task in orphanTasks" :key="task.id">
            <EvolveTask @clicked="showTaskModal" :task="task"></EvolveTask>
          </div>
        </div>
        <div class="goals">
          <div class="skeleton-placeholder" v-if="!goals">
            <div class="skeleton-item" v-for="n in 2" :key="n"></div>
          </div>
          <div
            v-else-if="
              goals && goals.length === 0 && upcomingBookings.length === 0
            "
          >
            <p class="booking-text">
              Sorry you don't have any goals set yet, reach out to coach to get
              some
            </p>
          </div>
          <div
            v-else-if="
              goals &&
                goals.length === 0 &&
                orphanTasks.length === 0 &&
                upcomingBookings.length > 0
            "
          >
            <div class="no-goals-container">
              <img
                class="sun-img"
                src="/img/coaching-platform/sun-cropped.svg"
              />
              <div class="no-goals-yet">
                <div v-if="isProfileCompleted" class="row">
                  <div class="col-8 text-container">
                    <div class="no-goal-title">Hold Tight!</div>
                    <div class="no-goal-text">
                      Once you have had your kick-off session with
                      {{ upcomingBookings[0].coach_title }}, they will put your
                      goals here along with some recommended resources and
                      worksheets that will help you on your journey to achieving
                      your goals.
                    </div>
                  </div>
                  <div
                    class="col-4 d-flex justify-content-center align-items-center img-container"
                  >
                    <img src="/img/coaching-platform/drinking-coffee.png" />
                  </div>
                </div>
                <b-spinner
                  v-else-if="isProfileDataLoading"
                  class="brand-primary"
                ></b-spinner>
                <CoachingProfileDetails
                  v-else
                  :userProfile="userProfile"
                  @updateProfile="updateProfile"
                />
              </div>
            </div>
          </div>
          <div v-else-if="goals && goals.length" class="goals mb-5">
            <div v-for="goal in goals" :key="goal.id">
              <EvolveGoal @clicked="showTaskModal" :goal="goal"></EvolveGoal>
            </div>
          </div>
        </div>
      </div>
      <div class="platform-right">
        <ConversationWrapper />
      </div>
    </div>
    <b-modal
      id="task-modal"
      size="lg"
      class="task-modal"
      hide-footer
      hide-header
      @hide="closeTaskModal"
    >
      <div>
        <span class="close-button float-right" @click="closeTaskModal"
          ><i class="fas fa-times brand-text fa-4x mr-4 mt-3"></i
        ></span>
        <ContentParser
          :id="taskModalId"
          :selectedDate="selectedDate"
          v-on:closeJournal="closeBothModals"
          v-on:setAsJournal="setAsJournal"
          v-on:selected-date="selectDate"
        />
      </div>
    </b-modal>
    <b-modal class="task-modal mt-5" id="confirm-modal" hide-footer hide-header>
      <div class="mb-4">
        <h3 class="confirm-modal-header">Close Without Saving</h3>
        <p class="brand-text">
          Are you sure you want to close this entry? You will lose any unsaved
          information.
        </p>
        <h4 class="brand-text">All changes will be lost</h4>
      </div>
      <div>
        <div class="d-flex justify-content-around align-items-center">
          <b-button
            class="confirm-modal-button outline-button"
            @click="closeBothModals"
            >Close without saving</b-button
          >
          <b-button
            class="bg-brand-highlight-1 confirm-modal-button cta-button"
            @click="closeConfirmModal"
            >Take me back</b-button
          >
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from "axios";
import config from "../../config/constants";
import CoachingProfileDetails from "./CoachingProfileDetails.vue";
import EvolveGoal from "./EvolveGoal.vue";
import EvolveTask from "./EvolveTask.vue";
import ConversationWrapper from "./ConversationWrapper.vue";
import EventBus from "@/helpers/eventbus";
import ContentParser from "./ContentParsers/ContentParser.vue";
import BookingWithCoachCard from "./BookingWithCoachCard.vue";

export default {
  name: "CoachingPlatform",
  components: {
    CoachingProfileDetails,
    EvolveGoal,
    ConversationWrapper,
    ContentParser,
    BookingWithCoachCard,
    EvolveTask
  },
  data() {
    return {
      isCoachingDataLoading: false,
      isProfileDataLoading: false,
      userProfile: null,
      isProfileCompleted: null,
      coachingData: null,
      taskModalId: null,
      selectedDate: null
    };
  },
  async mounted() {
    await this.getCoachingData();
    EventBus.$on("getCoachingData", this.getCoachingData);
  },
  computed: {
    goals() {
      return this.coachingData?.tasks?.goals;
    },
    orphanTasks() {
      return this.coachingData?.tasks?.orphans;
    },
    upcomingBookings() {
      return this.coachingData?.bookings?.upcoming;
    }
  },
  methods: {
    async getCoachingData() {
      if (this.isCoachingDataLoading) return;
      try {
        this.isCoachingDataLoading = true;
        const payload = {
          user_id: this.$store.getters.user_id
        };
        const res = await axios.post(
          config.api_env + "/application/api/u/coaching/get-data",
          payload,
          config.options
        );
        if (res.data.success) {
          this.coachingData = res.data;
          this.isProfileCompleted = res.data.user_profile_completed;
          if (!this.isProfileCompleted) {
            await this.getProfileData();
          }
        } else if (res.data.err && res.data.err.includes("3994")) {
          this.$store.dispatch("logout");
        }
      } catch (err) {
        console.error(err);
      } finally {
        this.isCoachingDataLoading = false;
      }
    },
    async getProfileData() {
      if (this.isProfileDataLoading) return;
      try {
        this.isProfileDataLoading = true;
        const payload = {
          user_id: this.$store.getters.user_id
        };
        const res = await axios.post(
          config.api_env + "/application/api/u/user/get-user-profile",
          payload,
          config.options
        );
        if (res.data.success) {
          this.userProfile = res.data?.user_profile;
        }
      } catch (err) {
        console.error(err);
      } finally {
        this.isProfileDataLoading = false;
      }
    },
    updateProfile() {
      this.isProfileCompleted = true;
    },
    showTaskModal(id, date, journal = false) {
      this.taskModalId = id;
      this.journalModal = journal;
      if (date === null) {
        this.selectedDate = null;
      } else {
        this.selectedDate = date;
      }
      this.$bvModal.show("task-modal");
    },
    closeTaskModal(evt) {
      if (this.journalModal) {
        evt.preventDefault();
        this.$bvModal.show("confirm-modal");
      } else {
        this.$bvModal.hide("task-modal");
      }
    },
    closeConfirmModal() {
      this.$bvModal.hide("confirm-modal");
    },
    selectDate(timestamp) {
      this.selectedDate = timestamp;
    },
    setAsJournal() {
      this.journalModal = true;
    },
    closeBothModals() {
      this.$bvModal.hide("confirm-modal");
      this.journalModal = false;
      this.$bvModal.hide("task-modal");
    }
  }
};
</script>

<style lang="less">
#CoachingPlatform {
  background-color: #f7f7f7;
  border-bottom: 1px solid #aeb0bd;

  .coaching-container {
    display: flex;
    gap: 38px;
    padding: 28px;
    max-width: 1170px;
    margin: 0 auto;
  }

  .booking-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 80px;
  }

  .heading-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin-bottom: 22px;
  }

  .heading {
    font-family: "PlusJakartaSansBold";
    color: #3a4a61;
    font-size: 2.6rem;
    margin-bottom: 0;
    text-align: left;
  }

  .line {
    flex: 1 1 auto;
    height: 2px;
    background-color: #c4c4c4;
  }

  .platform-left {
    flex: 1 1 100%;
  }

  .platform-right {
    max-width: 400px;
    min-width: 360px;
    margin: 0 auto;
  }

  .skeleton-placeholder {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .skeleton-item {
      height: 60px;
      background-color: #e0e0e0;
      border-radius: 8px;
      animation: pulse 1.5s infinite;
    }
  }

  .confirm-modal-header {
    font-size: 3rem;
    text-align: center;
    font-family: FilsonProBold, sans-serif;
    margin-bottom: 3rem;
  }

  .confirm-modal-button {
    width: 40%;
    border: 0px;
    box-shadow: 0 3px 3px rgba(0, 0, 0, 0.2);
  }

  .orphan-tasks {
    background-color: #7dc2ca;
    border-radius: 16px;
    padding: 15px 25px;
    p {
      color: white;
      font-size: 1.6rem;
      margin-bottom: 15px;
    }
  }

  .no-goals-container {
    .sun-img {
      height: 120px;
      width: 120px;
      z-index: 1;
    }

    .no-goals-yet {
      box-shadow: 0px 5px 5px rgba(0, 0, 0, 0.2);
      background-color: #403d56;
      color: white;
      position: relative;
      left: 30px;
      top: -60px;
      z-index: 2;
      width: 90%;
      padding: 28px 22px;
      border-radius: 20px;

      img {
        width: 100%;
      }

      .img-container {
        padding-left: 0 !important;
      }

      .text-container {
        padding-right: 3px;
      }

      .no-goal-title {
        font-size: 2.2rem;
        line-height: 3.4rem;
        font-family: "PlusJakartaSansBold";
        margin-bottom: 8px;
      }

      .no-goal-text {
        font-size: 1.6rem;
        padding-right: 0px;
        margin-right: 8px;
      }
    }
  }

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.6;
    }
  }
}

#confirm-modal {
  .confirmModalHeader {
    font-size: 3rem;
    text-align: center;
    font-family: FilsonProBold, sans-serif;
    margin-bottom: 3rem;
  }
  p {
    font-family: "PlusJakartaSansRegular";
    font-size: 1.5rem;
    text-align: center;
    margin-bottom: 2rem;
  }
  h4 {
    text-align: center;
    font-family: FilsonProBold, sans-serif;
    margin-bottom: 3rem;
  }
  .confirmModalButton {
    width: 40%;
    border: 0px;
    box-shadow: 0 3px 3px rgba(0, 0, 0, 0.2);
  }
  .modal-dialog {
    margin-top: 50px;
  }
  .modal-body {
    padding: 30px;
  }
}
#task-modal,
#confirm-modal {
  .modal-content {
    border-radius: 24px;
    overflow: hidden;
  }
  .modal-body {
    max-height: 96vh !important;
    overflow-x: hidden;
    overflow-y: auto;
  }
  .close-button {
    cursor: pointer;
  }
  .task-content {
    font-size: 1.6rem;
  }
  .task-complete-button {
    font-size: 1.6rem;
    padding-top: 10px;
    padding-bottom: 10px;
    min-width: 200px;
  }
  .task-modal-title {
    font-size: 3rem;
    font-weight: bold;
    text-align: center;
    padding: 30px 30px 0px 30px;
  }
  ::-webkit-scrollbar {
    padding-right: 10px;
    width: 13px;
    height: 13px;
  }
  ::-webkit-scrollbar-thumb {
    background: linear-gradient(40deg, #87d3dc 50%, #6b8d9d 100%);
    border-radius: 0px;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(40deg, #87d3dc 50%, #6b8d9d 100%);
  }
  ::-webkit-scrollbar-track {
    background: #fafafa;
    border-radius: 10px;
    box-shadow: inset 0px 0px 0px 0px #f0f0f0;
  }
}

.grad-icon {
  background: linear-gradient(to right, #985582, #d50870);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}
.grad-icon-completed {
  background: linear-gradient(to right, #8dcfd7, #529af4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}
.shadow {
  box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.15) !important;
}

@media screen and (max-width: 920px) {
  #CoachingPlatform {
    .coaching-container {
      flex-direction: column;
      padding: 24px;
    }

    .platform-right {
      max-width: 560px;
      min-width: 300px;
    }
  }
}
</style>
